// WorkflowSwitcher — the pill at the top of the rail that changes workflow (Figma 2026-08-27).
// "Change Workflow ›" inside a workflow; "Select your workflow ›" on the landing page (outline
// variant). Opens a menu listing Buyer & Target Research / Private Equity. Investment Banking is
// coming soon, so it is omitted from this menu (the landing card stays visible but disabled).
//
// It is a menu button, not a tablist: one visible trigger + a transient list of destinations.
// On the desktop rail the menu is `position: fixed` (the rail is `overflow: hidden`) and also opens
// on hover; in the mobile drawer it renders inline and is click-only.
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { ChevronRight, List } from 'lucide-react';
import {
  type ScoutVertical,
  type Workflow,
  WORKFLOW_META,
  SWITCHABLE_WORKFLOWS,
} from './sidebarNav';
import { useFlyoutPosition } from './useFlyoutPosition';

export interface WorkflowSwitcherProps {
  current: Workflow;
  /** False when the user lacks `pe:dataset` — the PE row renders locked. IB is not listed. */
  canPeDataset: boolean;
  onSelect: (workflow: ScoutVertical) => void;
}

export default function WorkflowSwitcher({
  current,
  canPeDataset,
  onSelect,
}: WorkflowSwitcherProps) {
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const pillRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { anchorRef, position, measure, isRail } = useFlyoutPosition<HTMLButtonElement>();

  const cancelClose = () => {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const isLanding = current === 'none';
  const label = isLanding ? 'Select your workflow' : 'Change Workflow';

  const openMenu = useCallback(() => {
    cancelClose();
    measure();
    setOpen(true);
  }, [measure]);

  const closeMenu = useCallback((returnFocus = false) => {
    cancelClose();
    setOpen(false);
    if (returnFocus) pillRef.current?.focus();
  }, []);

  // Clear any pending close on unmount so the timer can't fire against a dead component.
  useEffect(() => cancelClose, []);

  // Outside pointer dismiss, and re-measure while open so scroll/resize can't leave the menu behind.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node | null)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, measure]);

  const focusItem = (index: number) => {
    const items = itemRefs.current.filter(Boolean);
    if (items.length === 0) return;
    const wrapped = ((index % items.length) + items.length) % items.length;
    items[wrapped]?.focus();
  };

  const onPillKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      openMenu();
      // The menu mounts in this commit; focus once it exists.
      requestAnimationFrame(() => focusItem(0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      openMenu();
      requestAnimationFrame(() => focusItem(-1));
    } else if (e.key === 'Escape' && open) {
      e.preventDefault();
      closeMenu();
    }
  };

  const onItemKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusItem(index + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusItem(index - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusItem(0);
        break;
      case 'End':
        e.preventDefault();
        focusItem(-1);
        break;
      case 'Escape':
        e.preventDefault();
        closeMenu(true);
        break;
      case 'Tab':
        closeMenu();
        break;
      default:
    }
  };

  const select = (workflow: ScoutVertical, locked: boolean) => {
    if (locked) return;
    setOpen(false);
    onSelect(workflow);
  };

  // Hover-open is a desktop-rail affordance only; in the drawer the pill is click-only.
  //
  // The close is deferred: the flyout sits flush against the rail's outer edge, so travelling from
  // the pill to the panel briefly crosses rail area that belongs to neither. Closing instantly there
  // would make the items unreachable by mouse. Re-entering either element cancels the pending close.
  const hoverProps = {
    onMouseEnter: () => {
      if (isRail()) openMenu();
    },
    onMouseLeave: () => {
      if (!isRail()) return;
      cancelClose();
      closeTimer.current = setTimeout(() => setOpen(false), 180);
    },
  };

  return (
    <div className="wf-switcher" ref={rootRef} {...hoverProps}>
      <button
        type="button"
        ref={(node) => {
          pillRef.current = node;
          anchorRef.current = node;
        }}
        className={`wf-switcher-pill wf-switcher-pill--${isLanding ? 'outline' : 'filled'}`}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => (open ? closeMenu() : openMenu())}
        onKeyDown={onPillKeyDown}
      >
        <List className="sidebar-icon" aria-hidden="true" />
        <span className="wf-switcher-pill-label">{label}</span>
        <ChevronRight className="wf-switcher-chevron" aria-hidden="true" />
      </button>

      {open && (
        <ul
          id={menuId}
          role="menu"
          aria-label="Choose a workflow"
          className="wf-switcher-menu"
          // Fixed coords on the desktop rail so the menu escapes the rail's `overflow: hidden`.
          // In the drawer it stays in flow and CSS handles the layout.
          style={isRail() && position ? { top: position.top, left: position.left } : undefined}
          data-placement={isRail() ? 'fixed' : 'inline'}
        >
          {SWITCHABLE_WORKFLOWS.map((workflow, index) => {
            const meta = WORKFLOW_META[workflow];
            const locked = workflow === 'pe' && !canPeDataset;
            const active = workflow === current;
            return (
              <li key={workflow} role="none">
                <button
                  type="button"
                  role="menuitem"
                  ref={(node) => {
                    itemRefs.current[index] = node;
                  }}
                  className={`wf-switcher-item${active ? ' active' : ''}${locked ? ' locked' : ''}`}
                  aria-current={active ? 'true' : undefined}
                  aria-disabled={locked || undefined}
                  title={locked ? `${meta.label} requires PE dataset access` : undefined}
                  onClick={() => select(workflow, locked)}
                  onKeyDown={(e) => onItemKeyDown(e, index)}
                >
                  {meta.menuLabel}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
